import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AU_DEPART,
  arbreConnu,
  choisirLaProfondeur,
  fermerALaMolette,
  niveauxSous,
  ouvertesALaProfondeur,
  ouvrirALaMolette,
  type EtatDeProfondeur,
} from './profondeur';

/**
 * Ce qui est ouvert sur place, en Carte comme en Hiérarchie (ticket 13 de retours-testeurs) :
 * la profondeur choisie au sélecteur et ce que la molette ou la flèche a ouvert par-dessus.
 * Les règles sont dans `profondeur.ts` ; ce fichier les tient en mémoire pour la feuille.
 *
 * **Un seul état pour les deux vues**, comme la maquette validée : passer de la Carte à la
 * Hiérarchie garde la profondeur et ce qui est ouvert (il remplace les deux états séparés des
 * tickets 06 et 07). Il tient **tant qu'on reste dans le dossier**, en entrant dans une étape
 * ou en passant d'une vue à l'autre ; **quitter le dossier et y revenir referme tout**
 * (décision d'Igor du 2026-09-26) : la profondeur repart à 1.
 */
export interface ProfondeurIci {
  /** Autant de losanges que de niveaux sous le centre. */
  niveaux: number;
  /** Le losange allumé. */
  actuelle: number;
  /** Les étapes ouvertes sous le centre, dans l'ordre que la carte envoie au serveur. */
  ouvertes: readonly string[];
  choisir: (profondeur: number) => void;
  ouvrir: (id: string) => void;
  fermer: (id: string) => void;
}

interface Etat extends EtatDeProfondeur {
  dossier: string | undefined;
}

/**
 * Un autre dossier repart de zéro. Tant que la feuille se lit (`undefined`), on garde ce
 * qu'on a : on est peut-être toujours dans le même dossier.
 */
function duDossier(avant: Etat, dossierId: string | undefined): Etat {
  if (dossierId === undefined || avant.dossier === dossierId) return avant;
  if (avant.dossier === undefined) return { ...avant, dossier: dossierId };
  return { dossier: dossierId, ...AU_DEPART };
}

export function useProfondeur(
  dossierId: string | undefined,
  centreId: string,
  /** Les bulles connues sous le centre, dans l'ordre de lecture. */
  bulles: readonly { id: string; parentId: string | null }[]
): ProfondeurIci {
  const [etat, setEtat] = useState<Etat>({ dossier: dossierId, ...AU_DEPART });
  const courant = duDossier(etat, dossierId);

  /*
   * Le changement de dossier s'inscrit tout de suite, pas seulement au prochain geste :
   * passer dans un autre dossier par une étiquette, puis revenir par le retour du
   * navigateur, sans rien toucher entre les deux, referme aussi tout.
   */
  useEffect(() => {
    setEtat((avant) => duDossier(avant, dossierId));
  }, [dossierId]);

  const arbre = useMemo(() => arbreConnu(bulles), [bulles]);
  const niveaux = useMemo(() => niveauxSous(arbre, centreId), [arbre, centreId]);

  const changer = useCallback(
    (faire: (avant: EtatDeProfondeur) => EtatDeProfondeur) =>
      setEtat((avant) => {
        const ici = duDossier(avant, dossierId);
        return { ...faire(ici), dossier: ici.dossier };
      }),
    [dossierId]
  );

  const choisir = useCallback(
    (p: number) => changer((avant) => choisirLaProfondeur(avant, p, niveaux)),
    [changer, niveaux]
  );
  const ouvrir = useCallback(
    (id: string) => changer((avant) => ouvrirALaMolette(avant, arbre, centreId, id)),
    [changer, arbre, centreId]
  );
  const fermer = useCallback(
    (id: string) => changer((avant) => fermerALaMolette(avant, arbre, id)),
    [changer, arbre]
  );

  const { profondeur, molette, fermees } = courant;
  const ouvertes = useMemo(
    () => ouvertesALaProfondeur({ profondeur, molette, fermees }, arbre, centreId),
    [profondeur, molette, fermees, arbre, centreId]
  );

  return {
    niveaux,
    actuelle: Math.max(1, Math.min(niveaux, profondeur)),
    ouvertes,
    choisir,
    ouvrir,
    fermer,
  };
}
